// The tools a person pins to the top of their home page, at most four, in the order they pinned them.
// They are saved to the person's account when the server can store them (user.homePins comes back
// from /api/user as an array, or null when none were ever saved) and always kept on this computer
// too, so pins work before the account can store them and while the server is unreachable.
export const MAX_HOME_PINS = 4;

const PIN_PATTERN = /^\/[a-z0-9-]+(\/[a-z0-9-]+)*$/;
const MAX_PIN_LENGTH = 80;

// A clean list of portal paths: no duplicates, nothing malformed, at most four.
export function sanitizePins(value) {
  if (!Array.isArray(value)) return [];
  const pins = [];
  for (const pin of value) {
    if (pins.length === MAX_HOME_PINS) break;
    if (typeof pin === 'string' && pin.length <= MAX_PIN_LENGTH && PIN_PATTERN.test(pin) && !pins.includes(pin)) pins.push(pin);
  }
  return pins;
}

// Pin or unpin one tool. A fifth pin is refused rather than pushing another one out.
export function togglePin(pins, path) {
  if (pins.includes(path)) return { pins: pins.filter((pin) => pin !== path), refused: false };
  if (pins.length >= MAX_HOME_PINS) return { pins, refused: true };
  return { pins: [...pins, path], refused: false };
}

// Only the tools this person can open right now, in pin order.
export function visiblePins(pins, tools) {
  const paths = new Set((tools || []).map((tool) => tool.path));
  return sanitizePins(pins).filter((pin) => paths.has(pin));
}

function localKey(email) {
  return `rc-home-pins:${String(email || '').trim().toLowerCase()}`;
}

function defaultStorage() {
  try {
    return globalThis.localStorage || null;
  } catch {
    return null;
  }
}

export function readLocalPins(email, storage = defaultStorage()) {
  try {
    return sanitizePins(JSON.parse(storage.getItem(localKey(email)) || '[]'));
  } catch {
    return [];
  }
}

export function writeLocalPins(email, pins, storage = defaultStorage()) {
  try {
    storage.setItem(localKey(email), JSON.stringify(sanitizePins(pins)));
  } catch {
    // Storage is blocked (a private window): pins last for this visit only.
  }
}

// The account's pins when the server has them, otherwise this computer's.
export function initialPins(user, storage = defaultStorage()) {
  return Array.isArray(user?.homePins) ? sanitizePins(user.homePins) : readLocalPins(user?.email, storage);
}

// Pins made on this computer before the account could store them move to the account once:
// the account is ready (null, not absent) but has never saved any.
export function pinsToAdopt(user, storage = defaultStorage()) {
  if (!user || user.homePins !== null) return null;
  const local = readLocalPins(user.email, storage);
  return local.length ? local : null;
}

// Saves pins to the account. Resolves { where: 'account', pins } once saved there, or
// { where: 'device' } when the account cannot store pins yet (they stay on this computer).
// Rejects on any other failure.
export async function savePinsToAccount(pins, { accessToken, fetchImpl = globalThis.fetch } = {}) {
  const response = await fetchImpl('/api/home-pins', {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${accessToken}` },
    body: JSON.stringify({ pins }),
  });
  const body = await response.json().catch(() => null);
  if (response.status === 503 && body?.code === 'not_ready') return { where: 'device' };
  if (!response.ok) throw new Error(body?.error || `HTTP ${response.status}`);
  return { where: 'account', pins: sanitizePins(body?.pins) };
}

// Keeps the signed in user's copy of their account pins current, so a reload shows them at once.
export function rememberAccountPins(pins, storage = defaultStorage()) {
  try {
    const stored = JSON.parse(storage.getItem('user') || 'null');
    if (stored && typeof stored === 'object') storage.setItem('user', JSON.stringify({ ...stored, homePins: sanitizePins(pins) }));
  } catch {
    // Nothing to update.
  }
}
